import React from 'react';
import useCheckoutState from '../hooks/useCheckoutState';
import Button from '../components/Button';
import getButtonBackgroundColor from '../lib/getButtonBackgroundColor';
import getButtonTextColor from '../lib/getButtonTextColor';
import { getCheckoutConfirmation } from './ChargeStatusMessage/helpers';
import MultilineText from '../components/MultilineText';
import FilesList from '../components/FilesList/FilesList';
import request from '../services/request';
import { apiUrl } from '../services/env';
import downloadUrlAsFile from '../services/downloadUrlAsFile';
import getSellerPortalUrl from '../lib/getSellerPortalUrl';

export function SubmissionStatusMessage({
  submission,
  checkout,
  seller,
  restartPayment,
  email,
  customer,
  preview,
  isConfirmation,
}) {
  switch (submission.status) {
    case 'pending':
      return (
        <div className="cp_status-wrapper text-center p-4 w-full">
          <h2 className="cp_status-title text-black mb-2">Submission pending</h2>
          <div className="cp_status-content mb-4">
            Your submission is pending. Please contact{' '}
            {seller.displayName || seller.name || 'the seller'} to confirm your submission.
          </div>
        </div>
      );
    case 'failed':
      return (
        <div className="cp_status-wrapper text-center p-4 w-full">
          <h2 className="cp_status-title text-black mb-2">Submission failed</h2>
          <div className="cp_status-content mb-4">
            Your submission failed and has not been processed.
            <br />
            Please try again.
          </div>
          <Button
            handleClick={() => restartPayment({ isConfirmation })}
            appearance="black"
            className="cp_button"
            size="full"
            backgroundColor={getButtonBackgroundColor({
              buttonBackgroundColor: checkout.buttonBackgroundColor,
              brandColor: seller.brandColor,
            })}
            color={getButtonTextColor({
              buttonTextColor: checkout.buttonTextColor,
              buttonBackgroundColor: checkout.buttonBackgroundColor,
              brandColor: seller.brandColor,
            })}
          >
            Try again
          </Button>
        </div>
      );
    case 'succeeded':
    default: {
      const { title, message, type } = getCheckoutConfirmation({
        checkout,
        charge: submission,
        seller,
        email,
      });

      const hasFiles = checkout.files && checkout.files.length > 0;

      return (
        <>
          <div className="cp_status-wrapper text-center p-4 w-full">
            <h2 className="cp_status-title text-black mb-2">
              <div
                className="flex justify-center mb-4 cp_status-icon"
                style={{
                  color: seller.brandColor,
                }}
              >
                <svg width="56" height="56" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="m19 28 6 6 12-12m18 6a27 27 0 1 1-54 0 27 27 0 0 1 54 0Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {title}
            </h2>
            <div className="cp_status-content mb-4">
              {type === 'html' ? (
                <MultilineText
                  className="cp_status-text"
                  dangerouslySetInnerHTML={{
                    __html: message,
                  }}
                />
              ) : (
                <MultilineText className="cp_status-text">{message}</MultilineText>
              )}
            </div>

            {hasFiles && checkout.showFilesList && (
              <div className="mb-8">
                <FilesList.List>
                  {submission.files?.map((file, index) => (
                    <FilesList.Item
                      key={file._id}
                      file={file}
                      isFirst={index === 0}
                      isPreview={preview}
                      onDownloadFile={async () => {
                        const response = await request(
                          apiUrl +
                            `/customers/${customer._id}/files/${file._id}/download?token=${customer.loginToken}`,
                        );

                        if (response?.data?.url) {
                          downloadUrlAsFile(response?.data?.url, file.name);
                        } else {
                          window.alert(
                            'Unable to download file. Please try again or contact support.',
                          );
                        }
                      }}
                    />
                  ))}
                </FilesList.List>
              </div>
            )}

            {hasFiles && checkout.showViewDownloadsButton && (
              <div className="mb-4">
                <Button
                  appearance="black"
                  to={`${getSellerPortalUrl(seller)}/${customer.loginToken}/submissions/${
                    submission._id
                  }`}
                  ignoreBasename
                  className="cp_button cp_view-downloads-button"
                  size="full"
                  target="_blank"
                  backgroundColor={getButtonBackgroundColor({
                    buttonBackgroundColor: checkout.buttonBackgroundColor,
                    brandColor: seller.brandColor,
                  })}
                  color={getButtonTextColor({
                    buttonTextColor: checkout.buttonTextColor,
                    buttonBackgroundColor: checkout.buttonBackgroundColor,
                    brandColor: seller.brandColor,
                  })}
                >
                  {checkout.viewDownloadsText}
                </Button>
              </div>
            )}
          </div>
        </>
      );
    }
  }
}

export default function SubmissionStatus({ submission, preview, isConfirmation }) {
  const { checkout, seller, restartPayment } = useCheckoutState();

  return (
    <SubmissionStatusMessage
      submission={submission}
      checkout={checkout}
      seller={seller}
      restartPayment={restartPayment}
      email={submission.customerEmail}
      customer={submission.customer}
      preview={preview}
      isConfirmation={isConfirmation}
    />
  );
}
